/* eslint-disable react/forbid-prop-types */
import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'src/redux/store';
import {
    Accordion, AccordionDetails, AccordionSummary,
    Box, Typography, alpha, TextField, Select, MenuItem,
    Button, Stack, CircularProgress, IconButton, Tooltip, Popover,
    Checkbox, FormControlLabel, Chip, Link,
} from '@mui/material';
import { QRCodeCanvas } from 'qrcode.react';
import Iconify from 'src/components/Iconify';
import { callApi } from 'src/utils/apiCall';
import useCustomSnackbar from 'src/hooks/useCustomSnackbar';
import useCopyToClipboard from 'src/hooks/use-copy-to-clipboard';
import useAuth from 'src/hooks/useAuth';
import { sendEngagementTestMail } from 'src/redux/slices/campaign';
import { sendWhatsappTemplateTestMessage } from 'src/redux/slices/whatsapp';
import uuidv4 from 'src/utils/uuidv4';
import { common } from 'src/theme/palette';
import {
    buildSendFormsPayload,
    buildFormUrl,
    isValidEmail,
    isValidPhone,
    resolveDefaultTemplates,
    whatsappTemplateLabel,
} from './quickSendUtils';
import SendFormsPreviewPanel from './SendFormsPreviewPanel';

const QR_CANVAS_ID = 'quick-send-step4-qr';

// Channels visible in the segmented control.
const CHANNELS = [
    { value: 'COPY', label: 'Copy link', icon: 'eva:link-2-outline' },
    { value: 'EMAIL', label: 'Email', icon: 'eva:email-outline' },
    { value: 'WHATSAPP', label: 'WhatsApp', icon: 'ic:baseline-whatsapp' },
    { value: 'QR', label: 'QR code', icon: 'eva:grid-outline' },
];

// Order matters — when a channel has just been actioned we suggest the
// next one DOWN this list that hasn't been touched yet. EMAIL is the
// strongest fall-through (carries the form natively), then WHATSAPP,
// then COPY (universal fallback), then QR (in-room scan).
const NEXT_CHANNEL_PRIORITY = ['EMAIL', 'WHATSAPP', 'COPY', 'QR'];

// Lightweight relative-time formatter. Drops to "just now" for <10s
// so the strip doesn't jitter the instant a send completes, then
// switches to absolute time after an hour because "73m ago" is less
// useful than a wall-clock anchor by then.
const formatRelative = (timestamp, now) => {
    if (!timestamp) return '';
    const diffMs = Math.max(0, now - timestamp);
    const sec = Math.floor(diffMs / 1000);
    if (sec < 10) return 'just now';
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

/**
 * Step 4 — Deliver.
 *
 * Post-action screen by design. By the time we mount, the parent has
 * already POSTed `/send-forms/` with `channels=['COPY_LINK']`, so
 * `formUrl` is populated and the link banner sits at the top of every
 * tab.
 *
 * Channel pills swap the delivery affordance below the banner:
 *   - Copy link  → big "copy" button (URL primed in clipboard already).
 *   - Email      → template-driven composer + Send (separate dispatch).
 *   - WhatsApp   → same shape as Email.
 *   - QR code    → render the QR with a Download button.
 *
 * Expiry pill is intentionally removed from Quick — it defaults to 7
 * days. Power-users tweak it in Advanced.
 */
const QuickSendStep4Deliver = ({
    practiceId,
    practice,
    audienceMode,
    selectedLead,
    selectedForms,
    practiceFieldValues,
    consentConfig,
    formUrl,
    formLinks,
    onSendComplete,
    isGeneratingLink,
    onGenerateLink,
    activeChannel,
    onChannelChange,
    expiryDays,
    emailTemplates,
    whatsappTemplates,
    emailTemplateId,
    setEmailTemplateId,
    whatsappTemplateId,
    setWhatsappTemplateId,
    setSending,
    highFidelityPdf,
    onLeadUpdated,
}) => {
    const { enqueueSnackbar } = useCustomSnackbar();
    const { copy } = useCopyToClipboard();
    const dispatch = useDispatch();
    const { user } = useAuth();

    // PR-10 item 11 — super-admin check piggybacks on the same
    // `practiceUser` slice the Engagement Messages tab uses (see
    // EmailTemplateDataGrid.jsx). The slice is hydrated on dashboard
    // mount; if it isn't (e.g. dialog opened via deep link), the
    // `find` simply returns undefined → isSuperUser stays false →
    // the link is not rendered. Defensive: no extra API call.
    const practiceUsersResults = useSelector(
        (state) => state.practiceUser?.practiceUser?.results || [],
    );
    const isSuperUser = useMemo(() => {
        if (!practiceUsersResults?.length || !user?.pk) return false;
        const match = practiceUsersResults.find((el) => el?.user && el.user.id === user.pk);
        return Boolean(match?.user?.is_superuser);
    }, [practiceUsersResults, user]);

    const isAnonymous = audienceMode === 'ANONYMOUS';
    const isCopyTab = activeChannel === 'COPY';
    const isQrTab = activeChannel === 'QR';
    const isEmailTab = activeChannel === 'EMAIL';
    const isWhatsAppTab = activeChannel === 'WHATSAPP';

    const patientFirstName = selectedLead?.first_name || '';
    const patientName = `${selectedLead?.first_name || ''} ${selectedLead?.last_name || ''}`.trim();
    const patientEmail = selectedLead?.lead_contact?.email || selectedLead?.email || '';
    const patientPhone = selectedLead?.lead_contact?.phone || selectedLead?.phone || '';

    const [adHocEmail, setAdHocEmail] = useState('');
    const [adHocPhone, setAdHocPhone] = useState('');

    // PR-10 item 4 — inline "Add email" / "Add phone" for non-anonymous
    // leads with missing contact info. State stays here (Step 4 is the
    // only consumer); on save we PATCH /practices/{id}/leads/{leadId}/
    // and bubble the updated lead back to the parent via `onLeadUpdated`
    // so the recipient strip flips to the new value immediately and the
    // canSendEmail / canSendWhatsApp gates re-evaluate.
    const [inlineEmailDraft, setInlineEmailDraft] = useState('');
    const [inlinePhoneDraft, setInlinePhoneDraft] = useState('');
    const [savingInlineContact, setSavingInlineContact] = useState(false);

    // PR-10 item 6 — "BCC me a copy" toggle. Session-only state per
    // brief. Email tab only — the WA tab doesn't have a BCC concept.
    const [bccMe, setBccMe] = useState(false);

    // PR-10 item 11 — preview metadata surfaced from PR-3's
    // SendFormsPreviewPanel via the new `onPreviewMeta` callback. Used
    // exclusively to render the super-admin "Open in Mailgun" link.
    const [previewMeta, setPreviewMeta] = useState(null);
    useEffect(() => {
        // Clear stale meta whenever the active template changes so a
        // Mailgun-mode link from template A can never bleed into the
        // preview pane for template B.
        setPreviewMeta(null);
    }, [emailTemplateId, whatsappTemplateId, activeChannel]);

    // ── PR-4 — "Send test to me" popover state ───────────────────────────
    // Mirrors the engagement-template send_test affordance from the
    // Engagement Messages tab but lives inline in the Quick-Send Step 4
    // action row so the practice user can sanity-check the rendered
    // template before firing it at the patient. Default recipient is
    // pulled from the auth profile (``user.email`` / ``user.phone``);
    // the field stays editable so testers can route to a shared inbox.
    const testBtnRef = useRef(null);
    const [testAnchorEl, setTestAnchorEl] = useState(null);
    const [testRecipient, setTestRecipient] = useState('');
    const [sendingTest, setSendingTest] = useState(false);

    const openTestPopover = () => {
        // Prefill defensively — the user object surfaces ``email`` reliably
        // but the phone field name varies across the codebase; fall back
        // through the known shapes so we don't render an empty popover
        // on WA tab when a phone is in fact on the profile.
        const defaultEmail = user?.email || '';
        const defaultPhone = user?.phone
            || user?.contact_phone
            || user?.user?.phone
            || '';
        setTestRecipient(isEmailTab ? defaultEmail : defaultPhone);
        setTestAnchorEl(testBtnRef.current);
    };
    const closeTestPopover = () => {
        if (sendingTest) return;
        setTestAnchorEl(null);
    };

    const canSendTest = (() => {
        if (!testRecipient) return false;
        if (isEmailTab) return isValidEmail(testRecipient);
        if (isWhatsAppTab) return isValidPhone(testRecipient);
        return false;
    })();

    const handleSendTest = () => {
        if (!canSendTest || sendingTest) return;
        setSendingTest(true);
        const callback = ({ success, message }) => {
            setSendingTest(false);
            if (success) {
                enqueueSnackbar(`Test sent to ${testRecipient}`, { variant: 'success' });
                setTestAnchorEl(null);
            } else {
                enqueueSnackbar(message || 'Failed to send test', { variant: 'error' });
            }
        };
        if (isEmailTab) {
            // Thread the wizard's currently-selected patient + the
            // pre-flight CR's access_token into the BE so the test
            // email renders with REAL patient data — analysts see
            // exactly what the patient would receive instead of the
            // demo "Sample Patient" placeholder. Both fields are
            // optional on the BE side; absence falls back to demo
            // context. For ANONYMOUS audience (no lead, no CR), we
            // simply omit both and the BE uses the demo context.
            dispatch(sendEngagementTestMail({
                practiceId,
                tempLateId: emailTemplateId,
                detail: {
                    email: testRecipient.trim(),
                    ...(selectedLead?.id ? { lead_id: selectedLead.id } : {}),
                    ...(formLinks?.[0]?.accessToken
                        ? { consent_token: formLinks[0].accessToken }
                        : {}),
                },
                callback,
            }));
        } else if (isWhatsAppTab) {
            // WABA id resolution: the FE never gets a top-level
            // ``whatsappBusinessAccount`` reference in this flow because the
            // Step 4 template dropdown is fed from the cross-practice
            // ``/api/v1/me/whatsapp-templates/`` aggregation. PR-4 patches
            // ``MyWhatsappTemplateListSerializer`` to expose
            // ``whatsapp_business_account_id`` on each row so we can
            // resolve the WABA from the picked template without a second
            // round-trip. ``activeWhatsappTemplate`` is the matched row
            // from the cached list (see ``useMemo`` below).
            const wabaId = activeWhatsappTemplate?.whatsapp_business_account_id
                || activeWhatsappTemplate?.whatsapp_business_account
                || activeWhatsappTemplate?.practice?.whatsapp_business_account_id
                || null;
            if (!wabaId) {
                enqueueSnackbar('Cannot send test — WhatsApp business account not resolved', { variant: 'error' });
                setSendingTest(false);
                return;
            }
            // Same real-patient-context pattern as the email test —
            // pass selectedLead.id + the pre-flight CR's access_token
            // so the test WhatsApp renders against REAL patient data.
            // Both ride via optional spread so ANONYMOUS audience
            // (no lead) and pre-flight-failed cases fall back to the
            // BE's demo-lead context without any FE branching.
            dispatch(sendWhatsappTemplateTestMessage({
                practiceId,
                whatsappBusinessAccountId: wabaId,
                templateId: whatsappTemplateId,
                detail: {
                    phone: testRecipient.trim(),
                    ...(selectedLead?.id ? { lead_id: selectedLead.id } : {}),
                    ...(formLinks?.[0]?.accessToken
                        ? { consent_token: formLinks[0].accessToken }
                        : {}),
                },
                callback,
            }));
        }
    };

    const primaryForm = selectedForms?.[0] || null;
    const primaryFormName = primaryForm?.name || '';

    // Default templates from practice.settings — only set once, only when
    // the parent has not already chosen. Auto-select rules (priority order):
    //   1. Practice-default from settings (whatever resolveDefaultTemplates
    //      returns). Highest signal of intent.
    //   2. If exactly ONE template is available for the channel, pick it.
    //      A single template is unambiguously the right choice; making the
    //      user click a dropdown with one option is friction theatre.
    //   3. Otherwise leave the select empty so the user picks.
    useEffect(() => {
        if (!emailTemplateId) {
            const def = resolveDefaultTemplates(practice, 'EMAIL');
            if (def) {
                setEmailTemplateId(def);
            } else if ((emailTemplates || []).length === 1) {
                setEmailTemplateId(emailTemplates[0].id);
            }
        }
        if (!whatsappTemplateId) {
            const def = resolveDefaultTemplates(practice, 'WHATSAPP');
            if (def) {
                setWhatsappTemplateId(def);
            } else if ((whatsappTemplates || []).length === 1) {
                setWhatsappTemplateId(whatsappTemplates[0].id);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [practice, emailTemplates, whatsappTemplates]);

    // We still memoise the active WA template lookup so we can pull the
    // WABA id off it for the preview endpoint URL. The previous inline
    // merge-tag resolver + activeEmailTemplate are gone — the BE preview
    // endpoint is now the single source of truth (PR-3 §3.4.2).
    const activeWhatsappTemplate = useMemo(() => (
        (whatsappTemplates || []).find((t) => String(t.id) === String(whatsappTemplateId)) || null
    ), [whatsappTemplates, whatsappTemplateId]);

    // The WhatsApp template list returned by /api/v1/me/whatsapp-templates/
    // already carries the parent WABA id as ``whatsapp_business_account_id``
    // (MyWhatsappTemplateListSerializer at apps/practice/me_template_views.py:102).
    // Pull it off the active template so we can build the nested preview URL
    // (BE path: /practices/{p}/whatsapp-business-accounts/{waba}/whatsapp-message-templates/{id}/preview/).
    // Fallback to ``whatsapp_business_account`` for parent components that pass
    // the per-practice nested serializer payload (which only surfaces the FK
    // without the ``_id`` suffix). No new Redux selector is introduced — the
    // data is already on the template object the parent loaded in Step 1.
    const whatsappBusinessAccountId = activeWhatsappTemplate?.whatsapp_business_account_id
        || activeWhatsappTemplate?.whatsapp_business_account
        || null;

    // Copy state — controls the "Copied!" checkmark on the link button.
    const [copied, setCopied] = useState(false);

    // ── Per-channel send history ────────────────────────────────────────────
    // Stamps every successful action (Send, Copy, Download). Drives the
    // pill green-dot, the persistent status strip below the channel card,
    // and the "Send again / Try {next}" affordances in the action row.
    // Cleared only when the dialog itself unmounts (parent reset path) —
    // we deliberately keep it across tab switches so the user can see at
    // a glance what they've already done.
    const [channelHistory, setChannelHistory] = useState({
        COPY: null, EMAIL: null, WHATSAPP: null, QR: null,
    });
    // Ticking clock so the relative timestamps stay live without
    // re-rendering the whole step on every state change. Only runs when
    // there's something to age — when ANY channel has been actioned.
    const [nowTick, setNowTick] = useState(() => Date.now());
    const hasAnyHistory = Boolean(
        channelHistory.COPY || channelHistory.EMAIL
            || channelHistory.WHATSAPP || channelHistory.QR,
    );
    useEffect(() => {
        if (!hasAnyHistory) return undefined;
        const id = setInterval(() => setNowTick(Date.now()), 15000);
        return () => clearInterval(id);
    }, [hasAnyHistory]);

    // Next-channel suggestion — first channel in priority order that has
    // NOT been actioned yet AND is different from the channel the user is
    // currently looking at (avoids "Also send via Email" while they're on
    // the Email tab). When everything's been touched once, returns null.
    const nextChannelSuggestion = useMemo(() => {
        const ch = NEXT_CHANNEL_PRIORITY.find((c) => (
            !channelHistory[c] && c !== activeChannel
        ));
        if (!ch) return null;
        return CHANNELS.find((c) => c.value === ch) || null;
    }, [channelHistory, activeChannel]);

    const handleCopy = async () => {
        if (!formUrl) return;
        const ok = await copy(formUrl);
        setCopied(Boolean(ok));
        if (ok) {
            setChannelHistory((prev) => ({
                ...prev,
                COPY: {
                    at: Date.now(),
                    count: (prev.COPY?.count || 0) + 1,
                    label: 'Link copied',
                },
            }));
        }
        enqueueSnackbar(ok ? 'Link copied' : 'Failed to copy', { variant: ok ? 'success' : 'error' });
    };

    const handleOpenForm = () => {
        if (!formUrl) return;
        try { window.open(formUrl, '_blank', 'noopener,noreferrer'); } catch { /* no-op */ }
    };

    // ── Channel send handlers ────────────────────────────────────────────────
    const [sendingLocal, setSendingLocal] = useState(false);

    const recipientEmail = isAnonymous ? adHocEmail.trim() : patientEmail;
    const recipientPhone = isAnonymous ? adHocPhone.trim() : patientPhone;

    const canSendEmail = !!recipientEmail
        && isValidEmail(recipientEmail)
        && !!emailTemplateId;
    const canSendWhatsApp = !!recipientPhone
        && isValidPhone(recipientPhone)
        && !!whatsappTemplateId;

    const hydrateSendResult = (resData) => {
        if (!onSendComplete) return;
        // Anonymous wizard sends return rows under ``anonymous_links``
        // (the BE deliberately chose a different key for the no-Lead
        // surface). Union both shapes here so the post-send Email /
        // WhatsApp dispatch on the anonymous lane sees the CRs too.
        const consentRequests = [
            ...(Array.isArray(resData?.consent_requests) ? resData.consent_requests : []),
            ...(Array.isArray(resData?.anonymous_links) ? resData.anonymous_links : []),
        ];
        const links = selectedForms.map((form, i) => {
            const cr = consentRequests.find((r) => Number(r?.form_id) === Number(form.id))
                || consentRequests[i]
                || null;
            const accessToken = cr?.access_token || null;
            const url = cr?.form_url || buildFormUrl(accessToken, consentConfig);
            // Option A — capture the CR id so subsequent channel sends
            // can reuse this same CR (one access_token, multiple
            // delivery channels). Without this, each Send creates a
            // fresh CR and the patient ends up with multiple links.
            return {
                form,
                accessToken,
                consentRequestId: cr?.id || null,
                url,
                expiresAt: cr?.expires_at || null,
            };
        });
        const firstUrl = links[0]?.url || '';
        onSendComplete({
            formUrl: firstUrl,
            links,
            expiresAt: links.map((l) => l.expiresAt).filter(Boolean).sort()[0] || null,
        });
    };

    // Option A helper — derive the consent_request_ids array from the
    // pre-flight formLinks so we can reuse the existing CR(s) rather
    // than creating a new one per channel send. Returns null when no
    // pre-flight CRs are available (e.g. ANONYMOUS or pre-flight failed)
    // so callers can omit the field and let the BE create-and-dispatch
    // path run as before.
    const reusableConsentRequestIds = useMemo(() => {
        if (isAnonymous) return null;
        const ids = (formLinks || [])
            .map((l) => l?.consentRequestId)
            .filter(Boolean);
        return ids.length > 0 ? ids : null;
    }, [isAnonymous, formLinks]);

    const handleSendEmail = async () => {
        if (!canSendEmail || sendingLocal) return;
        setSendingLocal(true);
        setSending(true);
        try {
            // Fresh idempotency key per channel-action.
            //   The Step 3 → Step 4 pre-flight already consumed the dialog's
            //   primary idempotency key (with channels=['COPY_LINK']).
            //   Reusing it here with channels=['EMAIL'] would 409 the BE
            //   (same key + different body hash), so each Send action gets
            //   its own key — double-clicks are still guarded by the
            //   `sendingLocal` flag.
            const sendKey = `${uuidv4()}-email`;
            // PR-10 item 6 — when "BCC me a copy" is ticked, prepend the
            // current user's email. `buildSendFormsPayload` dedupes so a
            // user already in a shared BCC list won't get the email twice.
            const bcc = (bccMe && user?.email) ? [user.email] : null;
            const payload = buildSendFormsPayload({
                audienceMode: isAnonymous ? 'ANONYMOUS' : 'ONE_PATIENT',
                channels: ['EMAIL'],
                selectedForms,
                expiresInHours: Math.max(1, Number(expiryDays) || 7) * 24,
                selectedLead: isAnonymous ? null : selectedLead,
                practiceFieldValues,
                adHocRecipientEmail: isAnonymous ? adHocEmail.trim() : null,
                emailTemplateId,
                bcc,
                idempotencyKey: sendKey,
                // Option A — reuse the pre-flight CR(s) so the patient
                // gets ONE link delivered via this channel (plus any
                // subsequent channels). Null when no pre-flight ran
                // (anonymous mode etc) → BE create-and-dispatch path.
                consentRequestIds: reusableConsentRequestIds,
                highFidelityPdf,
            });
            const res = await callApi(
                `/api/v1/practices/${practiceId}/consent-requests/send-forms/`,
                'post',
                payload,
            );
            const ok = res?.status === 200 || res?.status === 201 || res?.status === 202;
            if (ok) {
                hydrateSendResult(res?.data);
                setChannelHistory((prev) => ({
                    ...prev,
                    EMAIL: {
                        at: Date.now(),
                        count: (prev.EMAIL?.count || 0) + 1,
                        recipient: recipientEmail,
                        label: 'Email sent',
                    },
                }));
                enqueueSnackbar('Email sent', { variant: 'success' });
            } else {
                let detail = 'Failed to send email';
                if (typeof res?.data === 'string') detail = res.data;
                else if (typeof res?.data?.detail === 'string') detail = res.data.detail;
                else if (Array.isArray(res?.data?.ad_hoc_recipient_email)) detail = res.data.ad_hoc_recipient_email[0];
                else if (Array.isArray(res?.data?.lead)) detail = res.data.lead[0];
                enqueueSnackbar(detail, { variant: 'error' });
            }
        } catch {
            enqueueSnackbar('Failed to send email', { variant: 'error' });
        }
        setSendingLocal(false);
        setSending(false);
    };

    const handleSendWhatsApp = async () => {
        if (!canSendWhatsApp || sendingLocal) return;
        setSendingLocal(true);
        setSending(true);
        try {
            const sendKey = `${uuidv4()}-whatsapp`;
            const payload = buildSendFormsPayload({
                audienceMode: isAnonymous ? 'ANONYMOUS' : 'ONE_PATIENT',
                channels: ['WHATSAPP'],
                selectedForms,
                expiresInHours: Math.max(1, Number(expiryDays) || 7) * 24,
                selectedLead: isAnonymous ? null : selectedLead,
                practiceFieldValues,
                adHocRecipientPhone: isAnonymous ? adHocPhone.trim() : null,
                whatsappTemplateId,
                idempotencyKey: sendKey,
                // Option A — reuse the pre-flight CR(s) so the patient
                // gets ONE link delivered via WhatsApp (plus Email if
                // they hit Send Email too). Null when no pre-flight
                // (anonymous etc) → BE create-and-dispatch path.
                consentRequestIds: reusableConsentRequestIds,
                highFidelityPdf,
            });
            const res = await callApi(
                `/api/v1/practices/${practiceId}/consent-requests/send-forms/`,
                'post',
                payload,
            );
            const ok = res?.status === 200 || res?.status === 201 || res?.status === 202;
            if (ok) {
                hydrateSendResult(res?.data);
                setChannelHistory((prev) => ({
                    ...prev,
                    WHATSAPP: {
                        at: Date.now(),
                        count: (prev.WHATSAPP?.count || 0) + 1,
                        recipient: recipientPhone,
                        label: 'WhatsApp sent',
                    },
                }));
                enqueueSnackbar('WhatsApp message sent', { variant: 'success' });
            } else {
                let detail = 'Failed to send WhatsApp';
                if (typeof res?.data === 'string') detail = res.data;
                else if (typeof res?.data?.detail === 'string') detail = res.data.detail;
                else if (Array.isArray(res?.data?.ad_hoc_recipient_phone)) detail = res.data.ad_hoc_recipient_phone[0];
                else if (Array.isArray(res?.data?.lead)) detail = res.data.lead[0];
                enqueueSnackbar(detail, { variant: 'error' });
            }
        } catch {
            enqueueSnackbar('Failed to send WhatsApp', { variant: 'error' });
        }
        setSendingLocal(false);
        setSending(false);
    };

    // PR-10 item 4 — inline contact save. Same endpoint shape as the
    // legacy lead-detail edit panel:
    //   PATCH /api/v1/practices/{practiceId}/leads/{leadId}/
    //   body: { lead_contact: { email | phone } }
    // No invented endpoints, no new Redux thunk — we keep this dialog
    // self-contained.
    const handleSaveInlineContact = async (kind) => {
        if (savingInlineContact) return;
        if (!selectedLead?.id) return;
        const value = (kind === 'EMAIL' ? inlineEmailDraft : inlinePhoneDraft).trim();
        if (!value) return;
        const ok = kind === 'EMAIL' ? isValidEmail(value) : isValidPhone(value);
        if (!ok) {
            enqueueSnackbar(
                kind === 'EMAIL' ? 'That email doesn\'t look right' : 'That phone doesn\'t look right',
                { variant: 'error' },
            );
            return;
        }
        setSavingInlineContact(true);
        try {
            const body = {
                lead_contact: kind === 'EMAIL'
                    ? { ...(selectedLead?.lead_contact || {}), email: value }
                    : { ...(selectedLead?.lead_contact || {}), phone: value },
            };
            const res = await callApi(
                `/api/v1/practices/${practiceId}/leads/${selectedLead.id}/`,
                'patch',
                body,
            );
            const success = res?.status === 200 || res?.status === 201 || res?.status === 204;
            if (success) {
                enqueueSnackbar(
                    kind === 'EMAIL' ? 'Email saved' : 'Phone saved',
                    { variant: 'success' },
                );
                // Locally hydrate the lead so the recipient strip flips
                // immediately. Parent can opt to refetch by hooking the
                // `onLeadUpdated` callback — but the optimistic update is
                // enough for this screen because the Step-4 component
                // re-reads patientEmail / patientPhone every render.
                const merged = {
                    ...selectedLead,
                    lead_contact: {
                        ...(selectedLead?.lead_contact || {}),
                        [kind === 'EMAIL' ? 'email' : 'phone']: value,
                    },
                };
                if (typeof onLeadUpdated === 'function') {
                    try { onLeadUpdated(merged); } catch { /* best-effort */ }
                }
                // Clear the draft so the inline TextField unmounts.
                if (kind === 'EMAIL') setInlineEmailDraft('');
                else setInlinePhoneDraft('');
            } else {
                let detail = kind === 'EMAIL' ? 'Failed to save email' : 'Failed to save phone';
                if (typeof res?.data === 'string') detail = res.data;
                else if (typeof res?.data?.detail === 'string') detail = res.data.detail;
                else if (Array.isArray(res?.data?.lead_contact)) detail = res.data.lead_contact[0];
                else if (res?.data?.lead_contact?.email) detail = res.data.lead_contact.email[0] || detail;
                else if (res?.data?.lead_contact?.phone) detail = res.data.lead_contact.phone[0] || detail;
                enqueueSnackbar(detail, { variant: 'error' });
            }
        } catch {
            enqueueSnackbar('Failed to save contact', { variant: 'error' });
        }
        setSavingInlineContact(false);
    };

    const handleDownloadQR = () => {
        try {
            const canvas = document.getElementById(QR_CANVAS_ID);
            if (!canvas) return;
            const url = canvas.toDataURL('image/png');
            const a = document.createElement('a');
            a.href = url;
            const filename = (primaryFormName || 'form').replace(/\s+/g, '-').toLowerCase();
            a.download = `${filename}-qr.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setChannelHistory((prev) => ({
                ...prev,
                QR: {
                    at: Date.now(),
                    count: (prev.QR?.count || 0) + 1,
                    label: 'QR downloaded',
                },
            }));
        } catch {
            // best-effort
        }
    };

    // ── Render ───────────────────────────────────────────────────────────────
    const headlineRecipient = (() => {
        if (isAnonymous) return 'Public link';
        if (patientFirstName) return patientFirstName;
        return 'patient';
    })();

    return (
        <Box>
            <Typography
                sx={{
                    fontSize: '10px', fontWeight: 800, letterSpacing: '0.08em',
                    color: common.mintyZest, textTransform: 'uppercase', mb: 0.5,
                }}
            >
                Step 4 · Deliver
            </Typography>
            <Typography sx={{ fontSize: '15px', fontWeight: 700, mb: 1.5 }}>
                {isAnonymous
                    ? 'Your public link is ready.'
                    : `Link is ready for ${headlineRecipient}.`}
            </Typography>

            {/* ── Link banner ────────────────────────────────────────────────
                Always-visible URL + Copy/Open affordances. The whole point
                of pre-generating in Step 3 was so this banner is the first
                thing the user sees on Step 4 — no matter which channel they
                tap below, the link is one click away. */}
            {(() => {
                if (formUrl) {
                    return (
                        <Box
                            sx={{
                                p: 1.25, borderRadius: '10px', mb: 1.5,
                                bgcolor: alpha(common.mintyZest, 0.06),
                                border: '1px solid', borderColor: alpha(common.mintyZest, 0.2),
                                display: 'flex', alignItems: 'center', gap: 0.75,
                            }}
                        >
                            <Iconify icon="eva:link-2-outline" width={16} sx={{ color: common.mintyZest, flexShrink: 0 }} />
                            <Typography
                                sx={{
                                    flex: 1,
                                    fontFamily: 'ui-monospace,Menlo,monospace',
                                    fontSize: '12px', color: common.mintyZest,
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}
                            >
                                {formUrl}
                            </Typography>
                            {/* PR-10 item 7 — expiry note promoted from the
                                footer caption to the header strip so it
                                travels with the link banner, not the Send
                                button. Hidden on very narrow widths so it
                                can't crowd the action icons. */}
                            <Tooltip title="Tweak in Advanced">
                                <Typography
                                    sx={{
                                        display: { xs: 'none', sm: 'inline' },
                                        fontSize: '10.5px',
                                        color: alpha(common.mintyZest, 0.85),
                                        whiteSpace: 'nowrap',
                                        flexShrink: 0,
                                        mr: 0.25,
                                    }}
                                >
                                    Expires in {expiryDays} day{expiryDays === 1 ? '' : 's'}
                                </Typography>
                            </Tooltip>
                            <Tooltip title={copied ? 'Copied' : 'Copy link'}>
                                <IconButton
                                    size="small"
                                    onClick={handleCopy}
                                    sx={{ p: 0.5, color: copied ? common.mintyZest : 'text.disabled' }}
                                >
                                    <Iconify icon={copied ? 'eva:checkmark-fill' : 'eva:copy-outline'} width={14} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Open form in new tab">
                                <IconButton
                                    size="small"
                                    onClick={handleOpenForm}
                                    sx={{ p: 0.5, color: 'text.disabled' }}
                                >
                                    <Iconify icon="eva:external-link-outline" width={14} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    );
                }
                if (isGeneratingLink) {
                    return (
                        <Box
                            sx={{
                                p: 1.25, borderRadius: '10px', mb: 1.5,
                                bgcolor: alpha(common.mintyZest, 0.04),
                                border: '1px dashed', borderColor: alpha(common.mintyZest, 0.32),
                                display: 'flex', alignItems: 'center', gap: 1,
                            }}
                        >
                            <CircularProgress size={14} sx={{ color: common.mintyZest }} />
                            <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                                Generating link — almost there…
                            </Typography>
                        </Box>
                    );
                }
                return (
                    <Box
                        sx={{
                            p: 1.25, borderRadius: '10px', mb: 1.5,
                            bgcolor: alpha('#FFAB00', 0.08),
                            border: '1px solid', borderColor: alpha('#FFAB00', 0.32),
                            display: 'flex', alignItems: 'center', gap: 1,
                        }}
                    >
                        <Iconify icon="eva:alert-triangle-outline" width={16} sx={{ color: '#b76e00', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '12px', color: '#8a5a00', flex: 1 }}>
                            The pre-flight didn&apos;t produce a link.
                        </Typography>
                        <Button
                            size="small"
                            onClick={onGenerateLink}
                            sx={{
                                textTransform: 'none', fontSize: '11.5px', fontWeight: 700,
                                color: '#b76e00',
                                '&:hover': { bgcolor: alpha('#FFAB00', 0.12) },
                            }}
                        >
                            Retry
                        </Button>
                    </Box>
                );
            })()}

            {/* ── Channel pills ─────────────────────────────────────────────── */}
            <Box
                role="tablist"
                sx={{
                    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: 0.5, p: 0.5, borderRadius: '10px', mb: 1.5,
                    bgcolor: 'rgba(145,158,171,0.12)',
                }}
            >
                {CHANNELS.map((ch) => {
                    const active = activeChannel === ch.value;
                    const actioned = Boolean(channelHistory[ch.value]);
                    return (
                        <Button
                            key={ch.value}
                            role="tab"
                            aria-selected={active}
                            onClick={() => onChannelChange(ch.value)}
                            disableRipple
                            startIcon={<Iconify icon={ch.icon} width={14} />}
                            sx={{
                                position: 'relative',
                                minHeight: 30, py: 0.5, px: 0.75,
                                fontSize: '12px', fontWeight: 600, textTransform: 'none',
                                borderRadius: '7px',
                                color: active ? '#1C252E' : 'text.secondary',
                                bgcolor: active ? '#fff' : 'transparent',
                                boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                                '&:hover': {
                                    bgcolor: active ? '#fff' : 'rgba(145,158,171,0.16)',
                                },
                            }}
                        >
                            {ch.label}
                            {actioned && (
                                <Box
                                    aria-label="actioned"
                                    sx={{
                                        position: 'absolute',
                                        top: 3,
                                        right: 3,
                                        width: 14,
                                        height: 14,
                                        borderRadius: '50%',
                                        bgcolor: common.mintyZest,
                                        color: '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 0 0 2px #fff',
                                    }}
                                >
                                    <Iconify
                                        icon="eva:checkmark-fill"
                                        width={10}
                                        sx={{ color: '#fff' }}
                                    />
                                </Box>
                            )}
                        </Button>
                    );
                })}
            </Box>

            {/* ── Channel body ─────────────────────────────────────────────── */}
            <Box
                sx={{
                    border: '1px solid', borderColor: 'divider', borderRadius: '12px',
                    overflow: 'hidden', bgcolor: '#fff', p: 2,
                }}
            >
                {/* COPY ─ big primary CTA + reassurance ───────────────────── */}
                {isCopyTab && (
                    <Stack alignItems="center" gap={1.25} sx={{ py: 1.5 }}>
                        <Box
                            sx={{
                                width: 48, height: 48, borderRadius: '50%',
                                bgcolor: alpha(common.mintyZest, 0.1),
                                color: common.mintyZest,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                        >
                            <Iconify icon={copied ? 'eva:checkmark-circle-2-fill' : 'eva:link-2-outline'} width={26} />
                        </Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '14px' }}>
                            {copied ? 'Copied to clipboard' : 'Ready to share'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11.5px', maxWidth: 320, textAlign: 'center' }}>
                            Paste the link wherever you want — chat, SMS, email, anywhere.
                        </Typography>
                        <Button
                            onClick={handleCopy}
                            disabled={!formUrl}
                            startIcon={<Iconify icon={copied ? 'eva:checkmark-fill' : 'eva:copy-outline'} width={14} />}
                            sx={{
                                mt: 0.5,
                                bgcolor: common.mintyZest, color: '#fff', textTransform: 'none',
                                fontWeight: 700, borderRadius: '10px', px: 3, py: 0.875, fontSize: '13px',
                                '&:hover': { bgcolor: '#007B40' },
                                '&.Mui-disabled': { bgcolor: 'rgba(145,158,171,0.2)', color: '#919EAB' },
                            }}
                        >
                            {copied ? 'Copy again' : 'Copy link'}
                        </Button>
                        {/* Multi-form sequences are stitched together
                            patient-side — the primary CTA always copies
                            the head-of-sequence URL. PR-10 item 10 adds
                            a collapsed disclosure so power users CAN see
                            and copy the per-form URLs if they need to
                            spot-check one out of order. */}
                        {formLinks && formLinks.length > 1 && (
                            <>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: 'block', mt: 1.5,
                                        fontSize: '11.5px', color: 'text.secondary',
                                        textAlign: 'center',
                                    }}
                                >
                                    <Iconify
                                        icon="eva:layers-outline"
                                        width={14}
                                        sx={{ verticalAlign: '-2px', mr: 0.5, color: common.mintyZest }}
                                    />
                                    {formLinks.length} forms — the patient signs them in order from this one link.
                                </Typography>
                                <Accordion
                                    disableGutters
                                    elevation={0}
                                    sx={{
                                        mt: 1,
                                        width: '100%',
                                        bgcolor: 'transparent',
                                        border: '1px solid',
                                        borderColor: 'rgba(145,158,171,0.2)',
                                        borderRadius: '8px',
                                        '&:before': { display: 'none' },
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<Iconify icon="eva:chevron-down-fill" width={14} />}
                                        sx={{
                                            minHeight: 36,
                                            px: 1.25,
                                            '& .MuiAccordionSummary-content': { my: 0.5 },
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '11.5px', fontWeight: 600, color: 'text.secondary' }}>
                                            {formLinks.length} forms in this sequence (tap to see per-form links)
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ pt: 0, pb: 1, px: 1.25 }}>
                                        <Stack gap={0.5}>
                                            {formLinks.map((entry, idx) => {
                                                const formName = entry?.form?.name || `Form ${idx + 1}`;
                                                const url = entry?.url || '';
                                                return (
                                                    <Box
                                                        key={entry?.accessToken || entry?.url || idx}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.75,
                                                            py: 0.25,
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize: '11.5px',
                                                                fontWeight: 600,
                                                                color: 'text.primary',
                                                                flex: 1,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                            title={formName}
                                                        >
                                                            {idx + 1}. {formName}
                                                        </Typography>
                                                        <Tooltip title={url ? 'Copy link' : 'No link yet'}>
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    disabled={!url}
                                                                    onClick={async () => {
                                                                        if (!url) return;
                                                                        const okCopy = await copy(url);
                                                                        enqueueSnackbar(
                                                                            okCopy ? 'Link copied' : 'Failed to copy',
                                                                            { variant: okCopy ? 'success' : 'error' },
                                                                        );
                                                                    }}
                                                                    sx={{
                                                                        p: 0.5,
                                                                        color: url ? common.mintyZest : 'text.disabled',
                                                                    }}
                                                                >
                                                                    <Iconify icon="eva:copy-outline" width={14} />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                    </AccordionDetails>
                                </Accordion>
                            </>
                        )}
                    </Stack>
                )}

                {/* EMAIL / WHATSAPP ─ template picker + preview + Send ───── */}
                {(isEmailTab || isWhatsAppTab) && (
                    <Stack gap={1.25}>
                        {isAnonymous && (
                            <TextField
                                size="small"
                                label={isEmailTab ? 'Send to email' : 'Send to phone'}
                                value={isEmailTab ? adHocEmail : adHocPhone}
                                onChange={(e) => (isEmailTab
                                    ? setAdHocEmail(e.target.value)
                                    : setAdHocPhone(e.target.value))}
                                placeholder={isEmailTab ? 'patient@example.com' : '+44 7…'}
                                InputLabelProps={{ shrink: true }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '13px' } }}
                            />
                        )}
                        {!isAnonymous && (
                            <>
                                <Box
                                    sx={{
                                        p: 1, borderRadius: '8px',
                                        bgcolor: 'rgba(145,158,171,0.06)',
                                        fontSize: '12px', color: 'text.secondary',
                                    }}
                                >
                                    <Typography component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                        {patientName || 'Patient'}
                                    </Typography>
                                    {' · '}
                                    {isEmailTab
                                        ? (patientEmail || 'no email on file')
                                        : (patientPhone || 'no phone on file')}
                                </Box>
                                {/* PR-10 item 4 — inline "Add email" / "Add
                                    phone" when the lead has no contact
                                    on file. Eagerly persisting per memory
                                    `feedback_eager_lead_create` — we PATCH
                                    the lead now so subsequent sends + the
                                    rest of the CRM see the new contact. */}
                                {isEmailTab && !patientEmail && (
                                    <Stack direction="row" gap={0.75} alignItems="center">
                                        <TextField
                                            size="small"
                                            fullWidth
                                            value={inlineEmailDraft}
                                            onChange={(e) => setInlineEmailDraft(e.target.value)}
                                            placeholder="Add email — patient@example.com"
                                            type="email"
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '13px' } }}
                                        />
                                        <Button
                                            size="small"
                                            variant="contained"
                                            disabled={savingInlineContact
                                                || !inlineEmailDraft
                                                || !isValidEmail(inlineEmailDraft)}
                                            onClick={() => handleSaveInlineContact('EMAIL')}
                                            sx={{
                                                bgcolor: common.mintyZest, color: '#fff',
                                                textTransform: 'none', fontWeight: 700,
                                                borderRadius: '8px', px: 1.5, fontSize: '12px',
                                                whiteSpace: 'nowrap',
                                                '&:hover': { bgcolor: '#007B40' },
                                                '&.Mui-disabled': {
                                                    bgcolor: 'rgba(145,158,171,0.2)',
                                                    color: '#919EAB',
                                                },
                                            }}
                                        >
                                            {savingInlineContact ? 'Saving…' : 'Save'}
                                        </Button>
                                    </Stack>
                                )}
                                {isWhatsAppTab && !patientPhone && (
                                    <Stack direction="row" gap={0.75} alignItems="center">
                                        <TextField
                                            size="small"
                                            fullWidth
                                            value={inlinePhoneDraft}
                                            onChange={(e) => setInlinePhoneDraft(e.target.value)}
                                            placeholder="Add phone — +44 7…"
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '13px' } }}
                                        />
                                        <Button
                                            size="small"
                                            variant="contained"
                                            disabled={savingInlineContact
                                                || !inlinePhoneDraft
                                                || !isValidPhone(inlinePhoneDraft)}
                                            onClick={() => handleSaveInlineContact('PHONE')}
                                            sx={{
                                                bgcolor: common.mintyZest, color: '#fff',
                                                textTransform: 'none', fontWeight: 700,
                                                borderRadius: '8px', px: 1.5, fontSize: '12px',
                                                whiteSpace: 'nowrap',
                                                '&:hover': { bgcolor: '#007B40' },
                                                '&.Mui-disabled': {
                                                    bgcolor: 'rgba(145,158,171,0.2)',
                                                    color: '#919EAB',
                                                },
                                            }}
                                        >
                                            {savingInlineContact ? 'Saving…' : 'Save'}
                                        </Button>
                                    </Stack>
                                )}
                            </>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontSize: '11.5px', fontWeight: 700, color: 'text.disabled', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                                Template
                            </Typography>
                            <Select
                                size="small"
                                value={(isEmailTab ? emailTemplateId : whatsappTemplateId) || ''}
                                onChange={(e) => (isEmailTab
                                    ? setEmailTemplateId(e.target.value)
                                    : setWhatsappTemplateId(e.target.value))}
                                displayEmpty
                                sx={{
                                    flex: 1, fontSize: '12.5px', height: 32,
                                    '& .MuiSelect-select': { py: 0.5, px: 1 },
                                    '& fieldset': { borderColor: 'rgba(145,158,171,0.3)' },
                                    borderRadius: '8px',
                                }}
                            >
                                <MenuItem value="" disabled>
                                    {(isEmailTab ? emailTemplates : whatsappTemplates)?.length
                                        ? 'Choose template'
                                        : 'No templates available'}
                                </MenuItem>
                                {((isEmailTab ? emailTemplates : whatsappTemplates) || []).map((t) => {
                                    // PR-10 item 5 — Default chip on the
                                    // practice's configured default template.
                                    // Default ids live on practice.settings
                                    // and are exposed via the same
                                    // resolveDefaultTemplates helper used in
                                    // index.jsx; consistent source of truth.
                                    const defaultId = resolveDefaultTemplates(
                                        practice,
                                        isEmailTab ? 'EMAIL' : 'WHATSAPP',
                                    );
                                    const isDefault = defaultId != null
                                        && String(defaultId) === String(t.id);
                                    // PR-10 item 8 — friendly WhatsApp label
                                    // fallback so we never show raw slugs.
                                    const label = isEmailTab
                                        ? (t.name || t.title || `Template ${t.id}`)
                                        : whatsappTemplateLabel(t);
                                    return (
                                        <MenuItem key={t.id} value={t.id} sx={{ fontSize: '12px' }}>
                                            <Stack direction="row" alignItems="center" gap={0.75}>
                                                <span>{label}</span>
                                                {isDefault && (
                                                    <Chip
                                                        size="small"
                                                        variant="outlined"
                                                        label="Default"
                                                        sx={{
                                                            height: 18,
                                                            fontSize: '10px',
                                                            borderColor: alpha(common.mintyZest, 0.5),
                                                            color: common.mintyZest,
                                                            '& .MuiChip-label': { px: 0.6 },
                                                        }}
                                                    />
                                                )}
                                            </Stack>
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </Box>

                        {/* PR-10 item 6 — "BCC me a copy". Session-only
                            state per spec; on send the BCC list is threaded
                            into the send-forms payload via the new `bcc`
                            field on buildSendFormsPayload. Email tab only;
                            WhatsApp has no analogous concept. */}
                        {isEmailTab && (
                            <FormControlLabel
                                control={(
                                    <Checkbox
                                        size="small"
                                        checked={bccMe}
                                        onChange={(e) => setBccMe(e.target.checked)}
                                        sx={{
                                            color: 'text.disabled',
                                            '&.Mui-checked': { color: common.mintyZest },
                                            p: 0.5,
                                        }}
                                    />
                                )}
                                label={(
                                    <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                                        BCC me a copy{user?.email ? ` (${user.email})` : ''}
                                    </Typography>
                                )}
                                sx={{ ml: -0.5, mt: -0.25 }}
                            />
                        )}

                        {/* Preview pane — server-rendered via PR-2 preview
                            endpoints. The previous inline regex covered only
                            5 of ~25 BE merge keys and rendered HTML as raw
                            characters; SendFormsPreviewPanel delegates to the
                            same renderer the BE uses at send-time, so preview
                            and send no longer drift. */}
                        {/* Real consent_token from the pre-flight CR so the
                            BE preview takes the real-CR context branch and
                            renders the ACTUAL form link in the body —
                            instead of the placeholder 00000000-0000-... UUID
                            the synthetic-lead path would have used. The CR
                            is already created by the wizard's pre-flight
                            (channels=['COPY_LINK']) before Step 4 mounts. */}
                        {isEmailTab ? (
                            <SendFormsPreviewPanel
                                practiceId={practiceId}
                                channel="EMAIL"
                                templateId={emailTemplateId}
                                leadId={selectedLead?.id || null}
                                formId={primaryForm?.id || null}
                                consentToken={formLinks?.[0]?.accessToken || null}
                                onPreviewMeta={setPreviewMeta}
                                recipientName={patientName}
                                recipientEmail={recipientEmail}
                                senderName={practice?.name || ''}
                            />
                        ) : (
                            <SendFormsPreviewPanel
                                practiceId={practiceId}
                                channel="WHATSAPP"
                                templateId={whatsappTemplateId}
                                whatsappBusinessAccountId={whatsappBusinessAccountId}
                                leadId={selectedLead?.id || null}
                                formId={primaryForm?.id || null}
                                consentToken={formLinks?.[0]?.accessToken || null}
                                onPreviewMeta={setPreviewMeta}
                            />
                        )}

                        {/* PR-10 item 11 — super-admin "Open in Mailgun"
                            link. Only renders when the previewed template
                            is Mailgun-mode (BE preview returns
                            is_mailgun_template=true) AND the current user
                            is a super-admin (matches the practice's
                            PracticeUser row + is_superuser flag — same
                            pattern as EmailTemplateDataGrid.jsx). The
                            mailgun_domain is best-effort plucked off the
                            practice payload; when absent we fall back to a
                            plain "template id: X" caption so the super-
                            admin can still copy the id manually. */}
                        {previewMeta?.is_mailgun_template && isSuperUser && previewMeta?.template_id && (
                            (() => {
                                const mailgunDomain = practice?.mailgun_domain
                                    || practice?.settings?.mailgun_domain
                                    || null;
                                if (mailgunDomain) {
                                    return (
                                        <Link
                                            href={`https://app.mailgun.com/app/sending/templates/${mailgunDomain}/${previewMeta.template_id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                                fontSize: '11px',
                                                color: common.mintyZest,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                            }}
                                        >
                                            <Iconify icon="eva:external-link-outline" width={12} />
                                            Open in Mailgun
                                        </Link>
                                    );
                                }
                                return (
                                    <Typography
                                        sx={{
                                            fontSize: '11px',
                                            color: 'text.disabled',
                                            fontFamily: 'ui-monospace,Menlo,monospace',
                                        }}
                                    >
                                        Mailgun template id: {previewMeta.template_id}
                                    </Typography>
                                );
                            })()
                        )}

                        {/* PR-10 item 9 — anonymous caveat promoted to a
                            full-width info banner at the bottom of the
                            channel-body card (above the action row). Was
                            previously a caption in the action row, easy
                            to miss. */}
                        {isAnonymous && (
                            <Stack
                                direction="row"
                                gap={0.75}
                                alignItems="flex-start"
                                sx={{
                                    width: '100%',
                                    mt: 0.25,
                                    p: 1,
                                    borderRadius: '8px',
                                    bgcolor: (theme) => alpha(theme.palette.info.main, 0.08),
                                    border: '1px solid',
                                    borderColor: (theme) => alpha(theme.palette.info.main, 0.24),
                                }}
                            >
                                <Iconify
                                    icon="eva:info-outline"
                                    width={16}
                                    sx={{ color: 'info.main', mt: '1px', flexShrink: 0 }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{ color: 'info.main', fontSize: '12.5px', fontWeight: 600 }}
                                >
                                    Anonymous — lead is auto-created when the form is submitted.
                                </Typography>
                            </Stack>
                        )}
                    </Stack>
                )}

                {/* QR ─ canvas + Download CTA ─────────────────────────────── */}
                {isQrTab && (
                    <Stack alignItems="center" gap={1.25} sx={{ py: 0.5 }}>
                        {formUrl ? (
                            <QRCodeCanvas
                                id={QR_CANVAS_ID}
                                value={formUrl}
                                size={148}
                                level="M"
                                includeMargin={false}
                                bgColor="#FFFFFF"
                                fgColor="#1C252E"
                            />
                        ) : (
                            <CircularProgress size={22} sx={{ color: common.mintyZest }} />
                        )}
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11.5px' }}>
                            Scan to open the form on a phone.
                        </Typography>
                    </Stack>
                )}
            </Box>

            {/* ── PDF fidelity — read-only readout ────────────────────────
                The fidelity flag is frozen onto each ConsentRequest at
                pre-flight time (Step 3 → Step 4). The editable Switch
                lives in Step 3; here we just acknowledge the locked-in
                choice so the user knows what's going to happen on submit.
                Only renders when the user opted out (the default ON case
                is silent to keep Quick Send minimal). */}
            {highFidelityPdf === false && (
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={0.75}
                    sx={{
                        mt: 1.25,
                        p: 0.75,
                        px: 1,
                        borderRadius: '8px',
                        bgcolor: 'rgba(145,158,171,0.06)',
                    }}
                >
                    <Iconify
                        icon="eva:file-text-outline"
                        width={14}
                        sx={{ color: 'text.disabled', flexShrink: 0 }}
                    />
                    <Typography sx={{ fontSize: '11px', color: 'text.secondary', flex: 1, lineHeight: 1.35 }}>
                        PDF will use the server-side fallback (set in Step 3).
                    </Typography>
                </Stack>
            )}

            {/* ── Channel status strip ──────────────────────────────────────
                Persistent at-a-glance summary of everything actioned this
                session — survives tab switches so the user can see they've
                already emailed and copied the link, even while looking at
                the QR tab. Pairs with the green dots on the channel pills
                above. The right-hand "Also send via X" button only renders
                while there's a channel that hasn't been touched yet; once
                everything's been actioned at least once, the nudge
                collapses and only the summary chips remain. */}
            {hasAnyHistory && (
                <Box
                    sx={{
                        mt: 1.25,
                        p: 1,
                        borderRadius: '10px',
                        bgcolor: alpha(common.mintyZest, 0.06),
                        border: '1px solid',
                        borderColor: alpha(common.mintyZest, 0.2),
                        // Always stack — status chips on top, the "Also send
                        // via X" suggestion underneath with a soft divider
                        // between them so it reads as a distinct secondary
                        // action, not an extension of the confirmation chip.
                        // Previously they sat in the same row and users were
                        // clicking the suggestion thinking it was part of the
                        // status (and vice-versa).
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        gap: 0.75,
                    }}
                >
                    <Stack
                        direction="row"
                        flexWrap="wrap"
                        gap={0.5}
                        sx={{ flex: 1, minWidth: 0 }}
                    >
                        {NEXT_CHANNEL_PRIORITY.map((key) => {
                            const entry = channelHistory[key];
                            if (!entry) return null;
                            const meta = CHANNELS.find((c) => c.value === key);
                            const relative = formatRelative(entry.at, nowTick);
                            const recipient = entry.recipient ? ` · ${entry.recipient}` : '';
                            const repeat = entry.count > 1 ? ` · ×${entry.count}` : '';
                            return (
                                <Tooltip
                                    key={key}
                                    title={`${entry.label}${recipient} · ${relative}`}
                                >
                                    <Chip
                                        size="small"
                                        icon={(
                                            <Iconify
                                                icon={meta?.icon || 'eva:checkmark-fill'}
                                                width={12}
                                            />
                                        )}
                                        label={`${entry.label}${recipient} · ${relative}${repeat}`}
                                        sx={{
                                            height: 22,
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            bgcolor: '#fff',
                                            color: common.mintyZest,
                                            border: '1px solid',
                                            borderColor: alpha(common.mintyZest, 0.3),
                                            '& .MuiChip-icon': {
                                                color: common.mintyZest,
                                                ml: '6px',
                                            },
                                            '& .MuiChip-label': {
                                                px: 0.75,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                maxWidth: 240,
                                            },
                                        }}
                                    />
                                </Tooltip>
                            );
                        })}
                    </Stack>
                    {nextChannelSuggestion && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                pt: 0.75,
                                borderTop: '1px dashed',
                                borderColor: alpha(common.mintyZest, 0.24),
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '11px',
                                    color: 'text.secondary',
                                    fontWeight: 500,
                                    flex: 1,
                                    minWidth: 0,
                                }}
                            >
                                Reach this patient another way?
                            </Typography>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => onChannelChange(nextChannelSuggestion.value)}
                                endIcon={(
                                    <Iconify icon="eva:arrow-forward-outline" width={12} />
                                )}
                                sx={{
                                    textTransform: 'none',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    color: common.mintyZest,
                                    borderColor: alpha(common.mintyZest, 0.4),
                                    borderRadius: '8px',
                                    px: 1.25,
                                    py: 0.375,
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0,
                                    '&:hover': {
                                        bgcolor: alpha(common.mintyZest, 0.08),
                                        borderColor: common.mintyZest,
                                    },
                                }}
                            >
                                {nextChannelSuggestion.value === 'COPY' && 'Copy link too'}
                                {nextChannelSuggestion.value === 'EMAIL' && 'Also send via Email'}
                                {nextChannelSuggestion.value === 'WHATSAPP' && 'Also send via WhatsApp'}
                                {nextChannelSuggestion.value === 'QR' && 'Show QR code'}
                            </Button>
                        </Box>
                    )}
                </Box>
            )}

            {/* ── Action row ─────────────────────────────────────────────────
                Spacing bumped to 2 (16px) so the labelled "Test send" button
                doesn't crowd the primary filled CTA. Top margin bumped too —
                an explicit gap between the green status strip above and the
                primary CTA cuts the visual ambiguity that was causing
                accidental double-clicks. */}
            <Stack
                direction="row"
                spacing={2}
                justifyContent="flex-end"
                alignItems="center"
                sx={{ mt: 2.5 }}
            >
                {/* PR-4 — "Send test to me" icon button.
                    Sits between the template select pane (above) and the
                    primary Send button so the existing primary-CTA layout is
                    not displaced. Disabled when no template has been picked
                    yet — without a template id there's nothing to send. */}
                {(isEmailTab || isWhatsAppTab) && (
                    <Tooltip title="Send a test to yourself before sending to the patient">
                        <span>
                            <Button
                                ref={testBtnRef}
                                size="small"
                                variant="outlined"
                                aria-label="Send a test to me"
                                disabled={(isEmailTab && !emailTemplateId)
                                    || (isWhatsAppTab && !whatsappTemplateId)}
                                onClick={openTestPopover}
                                startIcon={(
                                    <Iconify icon="eva:flash-outline" width={14} />
                                )}
                                sx={{
                                    // Labelled outlined button instead of an
                                    // icon-only square so the affordance is
                                    // unambiguous next to the primary filled
                                    // CTA — users were mis-clicking the icon
                                    // when it looked like a generic adjacent
                                    // button.
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '12.5px',
                                    color: common.mintyZest,
                                    borderColor: alpha(common.mintyZest, 0.4),
                                    borderRadius: '8px',
                                    px: 1.5,
                                    '&:hover': {
                                        bgcolor: alpha(common.mintyZest, 0.08),
                                        borderColor: common.mintyZest,
                                    },
                                    '&.Mui-disabled': {
                                        color: 'rgba(145,158,171,0.6)',
                                        borderColor: 'rgba(145,158,171,0.24)',
                                    },
                                }}
                            >
                                Test send
                            </Button>
                        </span>
                    </Tooltip>
                )}
                {isEmailTab && (
                    <Button
                        variant="contained"
                        disabled={!canSendEmail || sendingLocal}
                        onClick={handleSendEmail}
                        endIcon={sendingLocal
                            ? <CircularProgress size={12} sx={{ color: '#fff' }} />
                            : (
                                <Iconify
                                    icon={channelHistory.EMAIL
                                        ? 'eva:refresh-outline'
                                        : 'eva:paper-plane-outline'}
                                    width={14}
                                />
                            )}
                        sx={{
                            bgcolor: common.mintyZest, color: '#fff', textTransform: 'none',
                            fontWeight: 700, borderRadius: '8px', px: 2.5,
                            '&:hover': { bgcolor: '#007B40' },
                            '&.Mui-disabled': { bgcolor: 'rgba(145,158,171,0.2)', color: '#919EAB' },
                        }}
                    >
                        {(() => {
                            if (sendingLocal) return 'Sending…';
                            if (channelHistory.EMAIL) return 'Send email again';
                            return 'Send email';
                        })()}
                    </Button>
                )}
                {isWhatsAppTab && (
                    <Button
                        variant="contained"
                        disabled={!canSendWhatsApp || sendingLocal}
                        onClick={handleSendWhatsApp}
                        endIcon={sendingLocal
                            ? <CircularProgress size={12} sx={{ color: '#fff' }} />
                            : (
                                <Iconify
                                    icon={channelHistory.WHATSAPP
                                        ? 'eva:refresh-outline'
                                        : 'eva:paper-plane-outline'}
                                    width={14}
                                />
                            )}
                        sx={{
                            bgcolor: common.mintyZest, color: '#fff', textTransform: 'none',
                            fontWeight: 700, borderRadius: '8px', px: 2.5,
                            '&:hover': { bgcolor: '#007B40' },
                            '&.Mui-disabled': { bgcolor: 'rgba(145,158,171,0.2)', color: '#919EAB' },
                        }}
                    >
                        {(() => {
                            if (sendingLocal) return 'Sending…';
                            if (channelHistory.WHATSAPP) return 'Send WhatsApp again';
                            return 'Send WhatsApp';
                        })()}
                    </Button>
                )}
                {isQrTab && (
                    <Button
                        variant="contained"
                        disabled={!formUrl}
                        onClick={handleDownloadQR}
                        endIcon={(
                            <Iconify
                                icon={channelHistory.QR
                                    ? 'eva:refresh-outline'
                                    : 'eva:download-outline'}
                                width={14}
                            />
                        )}
                        sx={{
                            bgcolor: common.mintyZest, color: '#fff', textTransform: 'none',
                            fontWeight: 700, borderRadius: '8px', px: 2.5,
                            '&:hover': { bgcolor: '#007B40' },
                            '&.Mui-disabled': { bgcolor: 'rgba(145,158,171,0.2)', color: '#919EAB' },
                        }}
                    >
                        {channelHistory.QR ? 'Download again' : 'Download PNG'}
                    </Button>
                )}
                {/* PR-10 item 9 — anonymous caveat moved into the channel
                    body card above this action row as a body2 info banner.
                    The duplicate caption that lived here is removed. */}
            </Stack>

            {/* PR-4 — Test-send popover.
                Anchored to the flask icon button in the action row above.
                Lives outside the Stack so MUI Portal mounts it at the document
                root without breaking the action-row's flex layout. */}
            <Popover
                open={Boolean(testAnchorEl)}
                anchorEl={testAnchorEl}
                onClose={closeTestPopover}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { p: 1.5, width: 280, borderRadius: '10px' } }}
            >
                <Typography sx={{ fontSize: '12.5px', fontWeight: 700, mb: 0.75 }}>
                    {isEmailTab ? 'Send test email to' : 'Send test WhatsApp to'}
                </Typography>
                <TextField
                    size="small"
                    fullWidth
                    autoFocus
                    value={testRecipient}
                    onChange={(e) => setTestRecipient(e.target.value)}
                    placeholder={isEmailTab ? 'you@example.com' : '+44 7…'}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '12.5px' } }}
                />
                <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'text.secondary', fontSize: '11px', lineHeight: 1.4 }}>
                    We&apos;ll send a test with sample patient data — your real recipient won&apos;t be touched.
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
                    <Button
                        size="small"
                        onClick={closeTestPopover}
                        disabled={sendingTest}
                        sx={{ textTransform: 'none', fontSize: '12px', color: 'text.secondary' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={handleSendTest}
                        disabled={!canSendTest || sendingTest}
                        startIcon={sendingTest
                            ? <CircularProgress size={10} sx={{ color: '#fff' }} />
                            : <Iconify icon="eva:paper-plane-outline" width={12} />}
                        sx={{
                            bgcolor: common.mintyZest, color: '#fff', textTransform: 'none',
                            fontWeight: 700, borderRadius: '8px', px: 1.75, fontSize: '12px',
                            '&:hover': { bgcolor: '#007B40' },
                            '&.Mui-disabled': { bgcolor: 'rgba(145,158,171,0.2)', color: '#919EAB' },
                        }}
                    >
                        {sendingTest ? 'Sending…' : 'Send'}
                    </Button>
                </Stack>
            </Popover>

            {/* PR-10 item 7 — the "Link expires in X · Tweak in Advanced"
                caption that used to live here has been promoted into the
                link banner at the top of the step (right-aligned beside
                the form_url). The duplicate is removed. */}
        </Box>
    );
};

QuickSendStep4Deliver.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    practice: PropTypes.object,
    audienceMode: PropTypes.oneOf(['ONE_PATIENT', 'ANONYMOUS']).isRequired,
    selectedLead: PropTypes.object,
    selectedForms: PropTypes.array,
    practiceFieldValues: PropTypes.object,
    consentConfig: PropTypes.object,
    formUrl: PropTypes.string,
    formLinks: PropTypes.array,
    onSendComplete: PropTypes.func,
    isGeneratingLink: PropTypes.bool,
    onGenerateLink: PropTypes.func,
    activeChannel: PropTypes.oneOf(['EMAIL', 'WHATSAPP', 'QR', 'COPY']).isRequired,
    onChannelChange: PropTypes.func.isRequired,
    expiryDays: PropTypes.number,
    emailTemplates: PropTypes.array,
    whatsappTemplates: PropTypes.array,
    emailTemplateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    setEmailTemplateId: PropTypes.func.isRequired,
    whatsappTemplateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    setWhatsappTemplateId: PropTypes.func.isRequired,
    setSending: PropTypes.func.isRequired,
    highFidelityPdf: PropTypes.bool,
    // PR-10 item 4 — invoked after a successful inline lead-contact PATCH
    // so the parent can replace its `selectedLead` and keep state coherent
    // with the BE.
    onLeadUpdated: PropTypes.func,
};

QuickSendStep4Deliver.defaultProps = {
    practice: null,
    selectedLead: null,
    selectedForms: [],
    practiceFieldValues: {},
    consentConfig: null,
    formUrl: '',
    formLinks: [],
    onSendComplete: null,
    isGeneratingLink: false,
    onGenerateLink: null,
    expiryDays: 7,
    emailTemplates: [],
    whatsappTemplates: [],
    emailTemplateId: null,
    whatsappTemplateId: null,
    highFidelityPdf: true,
    onLeadUpdated: null,
};

export default QuickSendStep4Deliver;
